/**
 * visibleWhen — parse / serialize helpers for a question's `visibleWhen` condition string.
 *
 * ⚠️ MUST stay in sync with the backend evaluator:
 *   BAL/BAL/AppBAL/LHB/TaxQuestionPendingWalker.cs → `IsVisible(item, answeredByKey)`.
 * That method is the ONLY runtime evaluator (frozen / fail-open). It supports multiple
 * clauses joined by a single match mode:
 *   - ""/"Always" (case-insensitive)      → always visible
 *   - "field in a, b, c"                    → membership (split on the FIRST " in ", RHS split on ",")
 *   - "field not in a, b, c"                → negated membership (split on the FIRST " not in ")
 *   - "field = value"                       → equality   (split on the FIRST "=")
 *   - "field != value" / "field <> value"   → inequality (checked before "=")
 *   - "c1 AND c2 …"                         → visible only when EVERY clause matches
 *   - "c1 OR c2 …"                          → visible when AT LEAST ONE clause matches
 *   - anything else / unparseable          → treated as visible (advanced raw, edited as-is here)
 * Operators: `=`, `!=` (`<>`), `in`, `not in`; no numeric ops. Comparisons are case-insensitive.
 * Mixed AND+OR in one expression is out of scope → opens as advanced raw text.
 *
 * This module is pure logic (NO Vue / framework import) so it can be unit-tested under plain Node.
 */

/**
 * Operators supported by the backend evaluator, for populating an operator picker.
 * @type {{ value: string, title: string }[]}
 */
export const VISIBLE_WHEN_OPERATORS = [
  { value: '=', title: 'เท่ากับ (=)' },
  { value: '!=', title: 'ไม่เท่ากับ (!=)' },
  { value: 'in', title: 'เป็นหนึ่งใน (in)' },
  { value: 'not in', title: 'ไม่เป็นหนึ่งใน (not in)' },
];

/**
 * Match modes for joining multiple clauses of one question, for populating a mode picker.
 * @type {{ value: string, title: string }[]}
 */
export const VISIBLE_WHEN_MATCH_MODES = [
  { value: 'AND', title: 'ต้องเข้าทุกเงื่อนไข (AND)' },
  { value: 'OR', title: 'เข้าอย่างน้อย 1 เงื่อนไข (OR)' },
];

/**
 * @typedef {Object} VisibleWhenClause
 * @property {string} field       Left-hand identifier (fieldKey / ruleCode of a sibling question).
 * @property {'='|'!='|'in'|'not in'} operator
 * @property {string} value       Single value (operator '=' / '!=').
 * @property {string[]} values    Multiple values (operator 'in' / 'not in').
 */

/**
 * @typedef {Object} VisibleWhenState
 * @property {'always'|'conditional'|'advanced'} mode
 * @property {'AND'|'OR'} join                Match mode joining the clauses.
 * @property {VisibleWhenClause[]} conditions
 * @property {string} raw                     Canonical/original expression string.
 */

/**
 * Parse a single clause fragment into a structured clause.
 * (first " in " → membership; else first "=" → equality; else null)
 *
 * @param {*} part - The raw clause fragment (coerced to string).
 * @returns {VisibleWhenClause|null}
 */
export function parseClause(part) {
  const s = String(part == null ? '' : part).trim();
  if (!s) return null;

  // "field not in a, b, c" — split on the FIRST " not in " (checked BEFORE " in ")
  const notInIdx = s.toLowerCase().indexOf(' not in ');
  if (notInIdx > 0) {
    const field = s.substring(0, notInIdx).trim();
    const rhs = s.substring(notInIdx + 8).trim(); // " not in ".length === 8
    const values = rhs
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
    if (field && values.length >= 1) {
      return { field, operator: 'not in', value: '', values };
    }
  }

  // "field in a, b, c" — split on the FIRST " in " (case-insensitive)
  const inIdx = s.toLowerCase().indexOf(' in ');
  if (inIdx > 0) {
    const field = s.substring(0, inIdx).trim();
    const rhs = s.substring(inIdx + 4).trim();
    const values = rhs
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
    if (field && values.length >= 1) {
      return { field, operator: 'in', value: '', values };
    }
  }

  // "field != value" / "field <> value" — checked BEFORE "=" ("!=" contains "=")
  let neqIdx = s.indexOf('!=');
  if (neqIdx < 0) neqIdx = s.indexOf('<>');
  if (neqIdx > 0) {
    const field = s.substring(0, neqIdx).trim();
    const value = s.substring(neqIdx + 2).trim();
    if (field && value) {
      return { field, operator: '!=', value, values: [] };
    }
  }

  // "field = value" — split on the FIRST "="
  const eqIdx = s.indexOf('=');
  if (eqIdx > 0) {
    const field = s.substring(0, eqIdx).trim();
    const value = s.substring(eqIdx + 1).trim();
    if (field && value) {
      return { field, operator: '=', value, values: [] };
    }
  }

  return null;
}

/**
 * Serialize a single clause back to its canonical string, or null when incomplete.
 *
 * @param {Partial<VisibleWhenClause>} c
 * @returns {string|null}
 */
export function buildClause(c) {
  const field = String((c && c.field) || '').trim();
  if (!field) return null;

  if (c && (c.operator === 'in' || c.operator === 'not in')) {
    const vals = (c.values || []).map((v) => String(v).trim()).filter(Boolean);
    if (!vals.length) return null;
    return `${field} ${c.operator} ${vals.join(', ')}`;
  }

  const value = String((c && c.value) || '').trim();
  if (!value) return null;
  const op = c && c.operator === '!=' ? '!=' : '=';
  return `${field} ${op} ${value}`;
}

/**
 * Parse a stored `visibleWhen` expression into a structured builder state.
 * Mirrors {@link file://BAL/BAL/AppBAL/LHB/TaxQuestionPendingWalker.cs} `IsVisible`.
 *
 * @param {*} expr - The raw expression (coerced to string).
 * @returns {VisibleWhenState}
 */
export function parseVisibleWhen(expr) {
  const raw = String(expr == null ? '' : expr).trim();

  // ""/"Always" → always visible
  if (!raw || /^always$/i.test(raw)) {
    return { mode: 'always', join: 'AND', conditions: [], raw: 'Always' };
  }

  // Detect the join: OR wins over AND; mixed handled downstream (a mixed
  // expression won't fully parse under one split → advanced).
  let parts;
  let join;
  if (/\s+OR\s+/i.test(raw)) {
    parts = raw.split(/\s+OR\s+/i);
    join = 'OR';
  } else if (/\s+AND\s+/i.test(raw)) {
    parts = raw.split(/\s+AND\s+/i);
    join = 'AND';
  } else {
    parts = [raw];
    join = 'AND';
  }

  const conditions = parts.map(parseClause);
  if (parts.length >= 1 && conditions.every((c) => c !== null)) {
    return { mode: 'conditional', join, conditions, raw };
  }

  // Unparseable / mixed AND+OR → advanced (edited as raw text)
  return { mode: 'advanced', join: 'AND', conditions: [], raw };
}

/**
 * Serialize a builder state back to the canonical expression the backend evaluates.
 * Any incomplete conditional clause is dropped; an empty result degrades to 'Always'.
 *
 * @param {Partial<VisibleWhenState>} state
 * @returns {string}
 */
export function buildVisibleWhen(state) {
  const s = state || {};

  if (s.mode === 'always') return 'Always';

  if (s.mode === 'advanced') {
    return String(s.raw || '').trim() || 'Always';
  }

  if (s.mode === 'conditional') {
    const clauses = (s.conditions || []).map(buildClause).filter(Boolean);
    if (!clauses.length) return 'Always';
    if (clauses.length === 1) return clauses[0];
    return clauses.join(s.join === 'OR' ? ' OR ' : ' AND ');
  }

  return 'Always';
}

/**
 * Soft-validate a raw `visibleWhen` expression (advanced mode).
 * Splits on AND/OR and checks each part looks like a supported clause. When a
 * pool of known field keys is supplied, ALSO warns when a clause references a
 * field key outside that pool (a likely typo / wrong-case key).
 *
 * ⚠️ Advisory ONLY: this NEVER hard-blocks and NEVER changes parse/serialize
 * output. The runtime contract stays fail-open — the backend evaluator
 * ({@link file://BAL/BAL/AppBAL/LHB/TaxQuestionPendingWalker.cs} `IsVisible` →
 * `EvaluateClause`) treats an unknown/unanswered key as visible. Key matching
 * here is case-insensitive, consistent with the backend's OrdinalIgnoreCase
 * lookups, so a right-key/wrong-case reference does not raise a false warning.
 *
 * @param {*} raw
 * @param {string[]|Set<string>} [knownFieldKeys] - Pool of field keys the guided
 *   picker knows about. Omit (or pass empty) to skip the key-validity check.
 * @returns {string|null}
 */
export function visibleWhenWarning(raw, knownFieldKeys) {
  const v = String(raw == null ? '' : raw).trim();
  if (!v || /^always$/i.test(v)) return null;

  const parts = v.split(/\s+(?:AND|OR)\s+/i);
  const allValid = parts.every(
    (part) =>
      /[\w.]+\s*(?:!=|<>|=)\s*\S+/.test(part) ||
      /[\w.]+\s+(?:not\s+in|in)\s+\S+/i.test(part)
  );
  if (!allValid) {
    return 'รูปแบบอาจไม่ถูกต้อง — เช่น "field = value", "field != value", "field in a, b", "field not in a, b" เชื่อมด้วย AND / OR';
  }

  // Soft key-validity check against the known-field pool (advisory, opt-in).
  const pool = Array.isArray(knownFieldKeys)
    ? knownFieldKeys
    : (knownFieldKeys instanceof Set ? Array.from(knownFieldKeys) : null);
  if (pool && pool.length) {
    const known = new Set(
      pool.map((k) => String(k == null ? '' : k).trim().toLowerCase()).filter(Boolean)
    );
    const unknown = [];
    parts.forEach((part) => {
      const clause = parseClause(part);
      const field = clause && clause.field ? String(clause.field).trim() : '';
      if (field && !known.has(field.toLowerCase()) && !unknown.includes(field)) {
        unknown.push(field);
      }
    });
    if (unknown.length) {
      return `ไม่พบ field เหล่านี้ในรายการที่รู้จัก: ${unknown.join(', ')} — ตรวจสอบชื่อ field key อีกครั้ง (ยังบันทึกได้ตามปกติ)`;
    }
  }

  return null;
}

/**
 * Render a stored `visibleWhen` expression as a compact, plain-language Thai
 * preview for read-only surfaces (e.g. the question builder table's "Visible
 * when" column). Mirrors the guided dialog's live-preview logic by reusing
 * {@link parseVisibleWhen}, so the table reads the same way the editor does:
 *   - ""/"Always"        → "แสดงเสมอ"
 *   - conditional        → "แสดงเมื่อ <clauses joined by และ/หรือ>"
 *   - advanced / mixed   → the raw expression as-is (best effort)
 *
 * Advisory display ONLY — never parses for evaluation and never changes the
 * serialized value.
 *
 * @param {*} expr - The raw expression (coerced to string).
 * @returns {string}
 */
export function describeVisibleWhen(expr) {
  const state = parseVisibleWhen(expr);
  if (state.mode === 'always') return 'แสดงเสมอ';
  if (state.mode === 'advanced') {
    return String(state.raw || '').trim() || 'แสดงเสมอ';
  }
  const parts = (state.conditions || [])
    .map((c) => {
      const field = String((c && c.field) || '').trim();
      if (!field) return '';
      if (c.operator === 'in' || c.operator === 'not in') {
        const vals = (c.values || []).map((v) => String(v).trim()).filter(Boolean);
        if (!vals.length) return '';
        const lbl = c.operator === 'not in' ? 'ไม่เป็นหนึ่งใน' : 'เป็นหนึ่งใน';
        return `${field} ${lbl} ${vals.join(', ')}`;
      }
      const value = String((c && c.value) || '').trim();
      if (!value) return '';
      return c.operator === '!=' ? `${field} ≠ ${value}` : `${field} = ${value}`;
    })
    .filter(Boolean);
  if (!parts.length) return 'แสดงเสมอ';
  const sep = state.join === 'OR' ? ' หรือ ' : ' และ ';
  return `แสดงเมื่อ ${parts.join(sep)}`;
}
